import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Sparkles, MapPin, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { offersAPI } from '../services/api';
import { resolveApiErrorMessage } from '../utils/apiErrors';
import { OFFER_CITY_OVERRIDE_STORAGE_KEY, extractInterestItems, extractOfferItems, formatOfferDate, getOfferCategoryKey } from '../utils/offers';
import { cacheUtils } from '../utils/cache';
import { useLocalization } from '../context/LocalizationContext';
import { OfferCard } from '../components/offers/OfferCard';
import { InterestStatusBadge } from '../components/offers/InterestStatusBadge';

const CATEGORY_OPTIONS = ['ALL', 'REAL_ESTATE', 'ELECTRONICS_GADGETS', 'AUTOMOBILES', 'HOME_APPLIANCES'];
const SORT_OPTIONS = ['FEATURED', 'LATEST', 'EXPIRING_SOON'];

const compareDatesDescending = (left, right) => {
  const leftValue = new Date(left || 0).getTime();
  const rightValue = new Date(right || 0).getTime();
  return rightValue - leftValue;
};

const compareDatesAscending = (left, right) => {
  const leftValue = new Date(left || Number.MAX_SAFE_INTEGER).getTime();
  const rightValue = new Date(right || Number.MAX_SAFE_INTEGER).getTime();
  return leftValue - rightValue;
};

const sortOffers = (items, sortBy) => {
  const source = [...items];
  if (sortBy === 'LATEST') {
    return source.sort((left, right) => compareDatesDescending(left.validFrom, right.validFrom));
  }
  if (sortBy === 'EXPIRING_SOON') {
    return source.sort((left, right) => compareDatesAscending(left.validTill, right.validTill));
  }
  return source.sort((left, right) => {
    if (left.featured === right.featured) {
      return compareDatesDescending(left.validFrom, right.validFrom);
    }
    return left.featured ? -1 : 1;
  });
};

export const Offers = () => {
  const navigate = useNavigate();
  const { needsCompactFont, locale, language, t } = useLocalization();
  const [activeTab, setActiveTab] = useState('explore');
  const [offers, setOffers] = useState([]);
  const [featuredOffers, setFeaturedOffers] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [sortBy, setSortBy] = useState('FEATURED');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeTick, setTimeTick] = useState(0);

  useEffect(() => {
    try {
      localStorage.removeItem(OFFER_CITY_OVERRIDE_STORAGE_KEY);
    } catch (_error) {
      // Ignore storage cleanup errors.
    }
  }, []);

  useEffect(() => {
    if (!lastUpdated) return undefined;
    const timerId = window.setInterval(() => setTimeTick((value) => value + 1), 60000);
    return () => window.clearInterval(timerId);
  }, [lastUpdated]);

  useEffect(() => {
    let cancelled = false;

    const fetchOffersData = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const listParams = {
          page: 0,
          size: 24,
          sort: sortBy,
        };
        if (selectedCategory !== 'ALL') {
          listParams.category = selectedCategory;
        }

        const featuredParams = {
          ...listParams,
          featured: true,
          size: 3,
        };

        const [offersResponse, featuredResponse, interestsResponse] = await Promise.all([
          offersAPI.getOffers(listParams),
          offersAPI.getOffers(featuredParams),
          offersAPI.getMyOfferInterests({ page: 0, size: 20 }),
        ]);

        if (cancelled) return;

        const normalizedOffers = sortOffers(extractOfferItems(offersResponse.data), sortBy);
        const normalizedFeaturedOffers = extractOfferItems(featuredResponse.data).slice(0, 3);
        const normalizedInterests = extractInterestItems(interestsResponse.data);

        setOffers(normalizedOffers);
        setFeaturedOffers(normalizedFeaturedOffers);
        setInterests(normalizedInterests);
        setLastUpdated(Date.now());
      } catch (error) {
        if (cancelled) return;
        const message = resolveApiErrorMessage(error, t, t('offers.failedToLoad'));
        setErrorMessage(message);
        toast.error(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchOffersData();

    return () => {
      cancelled = true;
    };
  }, [language, selectedCategory, sortBy, t]);

  const openOfferDetails = (offer) => {
    if (!offer?.offerId) return;
    navigate(`/offers/${encodeURIComponent(offer.offerId)}`, {
      state: {
        offer,
      },
    });
  };
  const contextMessage = t('offers.contextUsingProfile');

  const interestsCountLabel = useMemo(
    () => t('offers.interestsCount', { count: interests.length }),
    [interests.length, t]
  );

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl fade-in" data-testid="offers-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-10 left-2 h-52 w-52 rounded-full bg-primary-200/30 blur-3xl" />
          <div className="absolute top-28 right-6 h-52 w-52 rounded-full bg-primary-200/25 blur-3xl" />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}>
              {t('common.offers')}
            </h1>
            <p className={`mt-2 app-page-subtitle max-w-3xl ${needsCompactFont ? 'leading-8 sm:text-sm' : ''}`}>{t('offers.subtitle')}</p>
                        {lastUpdated && (
              <p className="mt-2 app-page-meta">
                {t('common.lastUpdated', { time: cacheUtils.getTimeAgo(lastUpdated, locale, t) })}
              </p>
            )}
          </div>
        </div>

        <div className="app-highlight-surface p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-primary-700 dark:text-primary-200">
                <MapPin size={16} />
                <p className={`font-medium ${needsCompactFont ? 'text-[0.92rem] leading-7' : 'text-sm'}`}>{contextMessage}</p>
              </div>
              <div className="min-w-0 sm:min-w-[220px]">
                <label htmlFor="offers-sort" className="app-form-label">
                  {t('offers.sortLabel')}
                </label>
                <select
                  id="offers-sort"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  className="app-form-control"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(`offers.sorts.${option.toLowerCase()}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="max-w-md rounded-2xl border border-primary-200/80 bg-white/90 px-4 py-3 text-sm text-primary-800 shadow-sm dark:border-primary-900/40 dark:bg-slate-900/80 dark:text-slate-200">
              <div className="flex items-center gap-2 font-semibold text-primary-700 dark:text-primary-200">
                <Filter size={16} />
                <span>{t('offers.filterByCategory')}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {CATEGORY_OPTIONS.map((option) => {
                  const active = selectedCategory === option;
                  const translationKey =
                    option === 'ALL' ? 'offers.categories.all' : `offers.categories.${getOfferCategoryKey(option)}`;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setSelectedCategory(option)}
                      className={`rounded-full px-3 py-1.5 font-medium transition-colors ${
                        needsCompactFont ? 'text-[0.82rem] sm:text-[0.88rem]' : 'text-sm'
                      } ${
                        active
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'bg-primary-100 text-primary-800 hover:bg-primary-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {t(translationKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('explore')}
            className={`rounded-full px-4 py-2 font-semibold transition-colors ${
              needsCompactFont ? 'text-[0.84rem] sm:text-[0.9rem]' : 'text-sm'
            } ${
              activeTab === 'explore'
                ? 'bg-primary-500 text-white shadow-sm'
                : 'bg-primary-100 text-primary-800 hover:bg-primary-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {t('offers.exploreTab')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('interests')}
            className={`rounded-full px-4 py-2 font-semibold transition-colors ${
              needsCompactFont ? 'text-[0.84rem] sm:text-[0.9rem]' : 'text-sm'
            } ${
              activeTab === 'interests'
                ? 'bg-primary-500 text-white shadow-sm'
                : 'bg-primary-100 text-primary-800 hover:bg-primary-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {t('offers.myInterestsTab')}
          </button>
        </div>

        {!!errorMessage && !loading && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
            {errorMessage}
          </div>
        )}

        {loading ? (
          <div className="app-surface p-10">
            <div className="flex items-center justify-center gap-3 text-primary-800 dark:text-primary-200">
              <RefreshCw size={20} className="animate-spin text-primary-500" />
              <span>{t('offers.loading')}</span>
            </div>
          </div>
        ) : activeTab === 'explore' ? (
          <div className="space-y-6">
            {featuredOffers.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles size={18} className="text-primary-500" />
                  <div>
                    <h2 className={`app-panel-title ${needsCompactFont ? 'text-lg sm:text-xl leading-8' : ''}`}>{t('offers.featuredSectionTitle')}</h2>
                    <p className="app-panel-subtitle">{t('offers.featuredSectionSubtitle')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {featuredOffers.map((offer) => (
                    <OfferCard
                      key={`featured-${offer.offerId}`}
                      offer={offer}
                      locale={locale}
                      t={t}
                      needsCompactFont={needsCompactFont}
                      compact
                      onViewDetails={openOfferDetails}
                    />
                  ))}
                </div>
              </section>
            )}

            <section className="space-y-4">
              <div>
                <h2 className={`app-panel-title ${needsCompactFont ? 'text-lg sm:text-xl leading-8' : ''}`}>{t('offers.allOffersTitle')}</h2>
                <p className={`app-panel-subtitle ${needsCompactFont ? 'leading-7' : ''}`}>{t('offers.allOffersSubtitle')}</p>
              </div>
              {offers.length === 0 ? (
                <div className="app-surface p-10 text-center">
                  <h3 className="font-serif text-2xl font-semibold text-primary-900 dark:text-white">
                    {t('offers.noOffersTitle')}
                  </h3>
                  <p className="mt-2 text-sm text-primary-700 dark:text-primary-300">
                    {t('offers.noOffersSubtitle')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {offers.map((offer) => (
                    <OfferCard
                      key={offer.offerId}
                      offer={offer}
                      locale={locale}
                      t={t}
                      needsCompactFont={needsCompactFont}
                      onViewDetails={openOfferDetails}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        ) : (
          <section className="space-y-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className={`app-panel-title ${needsCompactFont ? 'text-lg sm:text-xl leading-8' : ''}`}>{t('offers.myInterestsTitle')}</h2>
                <p className={`app-panel-subtitle ${needsCompactFont ? 'leading-7' : ''}`}>{interestsCountLabel}</p>
              </div>
            </div>

            {interests.length === 0 ? (
              <div className="app-surface p-10 text-center">
                <h3 className="font-serif text-2xl font-semibold text-primary-900 dark:text-white">
                  {t('offers.noInterestsTitle')}
                </h3>
                <p className="mt-2 text-sm text-primary-700 dark:text-primary-300">
                  {t('offers.noInterestsSubtitle')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {interests.map((interest) => (
                  <article key={interest.referenceId || interest.offerId} className="app-surface p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-300">
                            {interest.brandName}
                          </p>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            {t(`offers.categories.${getOfferCategoryKey(interest.category)}`)}
                          </span>
                        </div>
                        <h3 className={`font-serif font-semibold tracking-tight text-primary-900 dark:text-white ${needsCompactFont ? 'text-[1.05rem] leading-7' : 'text-xl'}`}>
                          {interest.title}
                        </h3>
                        <div className={`grid gap-2 text-sm text-primary-700 dark:text-primary-200 sm:grid-cols-2 ${needsCompactFont ? 'leading-7' : ''}`}>
                          <p className={needsCompactFont ? 'leading-7' : ''}>
                            <span className="font-semibold text-primary-900 dark:text-white">{t('offers.referenceId')}:</span>{' '}
                            {interest.referenceId || t('common.noData')}
                          </p>
                          <p className={needsCompactFont ? 'leading-7' : ''}>
                            <span className="font-semibold text-primary-900 dark:text-white">{t('offers.submittedOn')}:</span>{' '}
                            {interest.submittedOn ? formatOfferDate(interest.submittedOn, locale) : t('common.noData')}
                          </p>
                          <p className={needsCompactFont ? 'leading-7' : ''}>
                            <span className="font-semibold text-primary-900 dark:text-white">{t('offers.locationLabel')}:</span>{' '}
                            {interest.city || t('offers.availableAcrossIndia')}
                          </p>
                          <p className={needsCompactFont ? 'leading-7' : ''}>
                            <span className="font-semibold text-primary-900 dark:text-white">{t('offers.validTill')}:</span>{' '}
                            {interest.validTill ? formatOfferDate(interest.validTill, locale) : t('offers.validWhileActive')}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-3 lg:items-end">
                        <InterestStatusBadge status={interest.interestStatus} t={t} />
                        <button
                          type="button"
                          onClick={() => openOfferDetails({ offerId: interest.offerId })}
                          className="app-secondary-button"
                        >
                          {t('offers.viewDetails')}
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Offers;
